import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, IconButton, Switch, Tooltip, useTheme } from "@mui/material";
import { EditRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, IServerSideDatasource } from "ag-grid-community";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { useHasScope } from "../../../config/globalConfig";
import { EXPIRED_DEFAULT_COL_DEF } from "./config";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import { GRID_LIST_PAGE_SIZE } from "../config";
import CustomLoader from "../../../util/component/CustomLoader";
import { snackbarTypes } from "../../../util/state/globalVariables";
import SectionHeader from "../../../util/component/SectionHeader";
import ContentBody from "../common/content-body/ContentBody";
import EditGrants from "./edit-grants/EditGrants";
import { fetchEnableRecoveryDmsList } from "./services/http.services";
import TenantForm from "../../booking/components/multitenant/TenantForm";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { IEnableRecoveryFilters } from "../types";

const EnableRecovery = () => {
    const hasScope = useHasScope;
    const theme = useTheme();
    const { t } = useTranslation(['network', 'translation']);
    const { globalData, dispatchGlobalData } = useContext(GlobalContext);

    const [openEditGrants, setOpenEditGrants] = useState<boolean>(false);
    const [recoveryLocations, setRecoveryLocations] = useState<any>([]);
    const gridRefER = useRef<AgGridReact>(null);
    const defaultColDef = useMemo<ColDef>(() => EXPIRED_DEFAULT_COL_DEF, []);

    const [tenants, setTenants] = useState([])

    const columnDefs = useMemo<ColDef[]>(() => {
        return getEnableRecoveryColumnDefs(t);
    }, [t]);

    const handleOpenEditGrants = () => {
        setOpenEditGrants(true);
    }

    const handleCloseEditGrants = () => {
        setOpenEditGrants(false);
    }

    const handleSaveEditGrants = () => {
        gridRefER.current?.api.refreshServerSide({ purge: true });
        handleCloseEditGrants();
    }

    const dmsListMutation = useMutation(fetchEnableRecoveryDmsList);

    const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(message),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const form = useForm<IEnableRecoveryFilters>({
        defaultValues:
        {
            tenants: globalData?.user.legalEntities?.map((elem: any) => elem.name)
        }
    })

    const tenantsWatch = useWatch<IEnableRecoveryFilters, 'tenants'>({
        control: form.control,
        name: "tenants"
    });

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            const reqParam: any = {
                bpCode: undefined,
                dmsName: undefined,
                bookAbility: undefined,
                state: undefined,
                paramRequest: {
                    pagination: (params.request.endRow !== undefined && params.request.startRow !== undefined) ?
                        (params.request.endRow - params.request.startRow) :
                        undefined,
                    startIndex: params.request.startRow,
                    endIndex: params.request.endRow,
                    sortBy: params.request.sortModel[0] ?
                        {
                            field: params.request.sortModel[0].colId,
                            order: params.request.sortModel[0].sort
                        } :
                        undefined
                },
                tenants:tenantsWatch
            }

            dmsListMutation.mutateAsync(reqParam)
                .then((response: any) => {
                    params.success({
                        rowData: response.recoveryLocations,
                        rowCount: response.params.totCount
                    });

                    if (response.params.totCount === 0) {
                        params.api.showNoRowsOverlay();
                    }

                    setOpenEditGrants(false);
                })
                .catch((err: AxiosError) => {
                    params.fail();
                    viewErrorMessage('customerData.message.error.tryLater');
                });
        }
    }), [tenantsWatch]);

    useEffect(() => {
        if (recoveryLocations) {
            const reqParam: any = {
                bpCode: undefined,
                dmsName: undefined,
                bookAbility: undefined,
                state: undefined,
                paramRequest: {
                    pagination: undefined,
                    startIndex: 0,
                    endIndex: undefined,
                    sortBy: undefined
                }
            }

            dmsListMutation.mutateAsync(reqParam)
                .then((response: any) => {
                    setRecoveryLocations(response.recoveryLocations);
                })
                .catch((err: AxiosError) => {
                    viewErrorMessage('customerData.message.error.tryLater');
                });
        }
    }, []);    

    return (
        <>
            <SectionHeader title={t('enableRecovery.header.title')} />

            <ContentBody>

                <FormProvider {...form}>
                    <Grid item xs={12} md={6} lg={3}>
                        <TenantForm
                            control={form.control}
                            name="tenants"
                            onChange={() => { }}
                        />
                    </Grid>
                </FormProvider>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mx: theme.spacing(1), mb: theme.spacing(1) }}>
                    <Tooltip arrow placement={"bottom"} title={t("enableRecovery.tooltip.edit")}>
                        <IconButton color={"accent"} onClick={handleOpenEditGrants}>
                            <EditRounded />
                        </IconButton>
                    </Tooltip>
                </Box>

                <AgGridCustomTable
                    gridRef={gridRefER}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    serverSideDatasource={serverSideDatasource}
                    rowModelType="serverSide"
                    serverSideInfiniteScroll
                    paginationPageSize={GRID_LIST_PAGE_SIZE}
                    cacheBlockSize={GRID_LIST_PAGE_SIZE}
                    suppressDragLeaveHidesColumns
                    suppressMultiSort
                    enableCellTextSelection
                />
            </ContentBody>

            <CustomLoader loading={dmsListMutation.isLoading} />

            <EditGrants onClose={handleCloseEditGrants} onSave={handleSaveEditGrants} open={openEditGrants} recoveryLocations={recoveryLocations} />
        </>
    )
}

export default EnableRecovery;

function getEnableRecoveryColumnDefs(t: any): ColDef[] {
    return [
        {
            field: 'dmsName',
            headerName: t('enableRecovery.table.headerName.dmsName'),
            sortable: true,
            resizable: true
        },
        {
            field: 'flagBookingRecovery',
            headerName: t('enableRecovery.table.headerName.bookingRecovery'),
            sortable: true,
            resizable: true,
            cellRenderer: (p: any) =>
                <Switch size={'medium'} checked={p.value} />
        },
        {
            field: 'flagRaRecovery',
            headerName: t('enableRecovery.table.headerName.raRecovery'),
            sortable: true,
            resizable: true,
            cellRenderer: (p: any) =>
                <Switch size={'medium'} checked={p.value} />
        },
        {
            field: 'flagCheckInRecovery',
            headerName: t('enableRecovery.table.headerName.checkInRecovery'),
            sortable: true,
            resizable: true,
            cellRenderer: (p: any) =>
                <Switch size={'medium'} checked={p.value} />
        },
        {
            field: 'flagSignRecovery',
            headerName: t('enableRecovery.table.headerName.signRecovery'),
            sortable: true,
            resizable: true,
            cellRenderer: (p: any) =>
                <Switch size={"medium"} checked={p.value} />
        }
    ];
}

